import { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2, Loader2, AlertCircle, Wrench, Users } from 'lucide-react';
import { useSkills, useCreateSkill, useDeleteSkill, type Skill } from '../hooks/useSkills';

function SkillRow({ skill, onDelete }: { skill: Skill; onDelete: (id: string) => void }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <div className="flex items-center justify-between p-3 bg-background-subtle rounded-lg">
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <Wrench className="h-4 w-4 text-primary" />
        </div>
        <div>
          <p className="font-medium text-sm text-foreground">{skill.name}</p>
          {skill.description && (
            <p className="text-xs text-foreground-muted">{skill.description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {skill.personCount !== undefined && (
          <span className="inline-flex items-center gap-1 text-xs text-foreground-muted">
            <Users className="h-3 w-3" />
            {skill.personCount}
          </span>
        )}
        {!showDeleteConfirm ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="p-1 text-foreground-muted hover:text-danger transition-colors rounded"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ) : (
          <div className="flex items-center gap-1">
            <button
              onClick={() => onDelete(skill.id)}
              className="px-2 py-1 text-xs font-medium text-white bg-danger rounded hover:bg-danger/90"
            >
              Delete
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-2 py-1 text-xs font-medium text-foreground-muted hover:text-foreground"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function SkillsManager() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillDescription, setNewSkillDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: skills = [], isLoading } = useSkills();
  const createMutation = useCreateSkill();
  const deleteMutation = useDeleteSkill();

  const handleCreateSkill = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSkillName.trim()) return;

    setError(null);
    createMutation.mutate(
      {
        name: newSkillName.trim(),
        description: newSkillDescription.trim() || undefined,
      },
      {
        onSuccess: () => {
          setNewSkillName('');
          setNewSkillDescription('');
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : 'Failed to create skill');
        },
      }
    );
  };

  const handleDeleteSkill = (id: string) => {
    setError(null);
    deleteMutation.mutate(id, {
      onError: (err) => {
        setError(err instanceof Error ? err.message : 'Failed to delete skill');
      },
    });
  };

  return (
    <div className="bg-background rounded-lg border border-border">
      {/* Header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-background-subtle transition-colors"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-foreground-muted" />
          ) : (
            <ChevronRight className="h-5 w-5 text-foreground-muted" />
          )}
          <h3 className="font-semibold text-foreground">Skills Management</h3>
          <span className="text-sm text-foreground-muted">({skills.length} skills)</span>
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border">
          {/* Error message */}
          {error && (
            <div className="mt-4 p-3 rounded-md bg-danger-subtle border border-danger text-sm text-danger-foreground flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          {/* Add new skill form */}
          <form onSubmit={handleCreateSkill} className="mt-4 p-3 bg-background-subtle rounded-lg">
            <h4 className="text-sm font-medium text-foreground mb-3">Add New Skill</h4>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                placeholder="Skill name"
                value={newSkillName}
                onChange={(e) => setNewSkillName(e.target.value)}
                required
                className="flex-1 px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={newSkillDescription}
                onChange={(e) => setNewSkillDescription(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <button
                type="submit"
                disabled={createMutation.isPending || !newSkillName.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary-hover rounded-md transition-colors disabled:opacity-50"
              >
                {createMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                Add
              </button>
            </div>
          </form>

          {/* Skills list */}
          <div className="mt-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : skills.length === 0 ? (
              <div className="text-center py-8 text-foreground-muted">
                <Wrench className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No skills defined yet</p>
                <p className="text-sm">Add skills above to assign them to people</p>
              </div>
            ) : (
              <div className="space-y-2">
                {skills.map((skill) => (
                  <SkillRow
                    key={skill.id}
                    skill={skill}
                    onDelete={handleDeleteSkill}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
